import * as React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, useToast } from "@chotca/ui";
import { vi } from "@chotca/config";
import { getSdk, useMe } from "@chotca/api-client";
import { setAccessToken } from "@/boot/access-token";
import { AppBootLoader } from "@chotca/app-shell";
import { AuthLayout } from "./auth-layout";
import { errMsg } from "./auth-errors";
import { readRedirectParam, resolvePostLoginUrl } from "./post-login";

export function LoginPage() {
  const t = useTranslations("auth");
  const { error: toastError } = useToast();
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [params] = React.useState(() => ({
    redirect: readRedirectParam(),
    worker:
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("worker") === "1",
  }));

  // Already signed in → bounce to dashboard/onboarding
  if (me.isLoading) return <AppBootLoader />;
  if (me.data) {
    return (
      <Navigate
        to={resolvePostLoginUrl({
          globalRole: me.data.globalRole,
          hasMembership: me.data.memberships.length > 0,
          redirect: params.redirect,
        })}
        replace
      />
    );
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    try {
      const res = await getSdk().auth.loginWithPassword({ email, password });
      setAccessToken(res.accessToken);
      qc.setQueryData(["me"], res.user);
      const url = resolvePostLoginUrl({
        globalRole: res.user.globalRole,
        hasMembership: res.user.memberships.length > 0,
        redirect: params.redirect,
      });
      if (url.startsWith("/")) {
        nav(url, { replace: true });
      } else {
        window.location.assign(url);
      }
    } catch (err) {
      const msg = errMsg(err, "Đăng nhập thất bại");
      setError(msg);
      toastError(msg);
      setPending(false);
    }
  }

  return (
    <AuthLayout
      label={vi.common.login}
      title={t("loginTitle")}
      subtitle={t("loginSubtitle")}
      footer={
        <>
          <span className="text-ink/65">Chưa có tài khoản? </span>
          <Link
            to="/register"
            className="font-bold text-ink underline-offset-4 hover:underline"
          >
            {vi.landing.employerCta}
          </Link>
        </>
      }
    >
      {params.worker ? (
        <div className="mb-6 rounded-2xl bg-mist p-4 text-sm text-ink/80">
          {t("workerHint")}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label={t("emailLabel")}>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
          />
        </FormField>
        <FormField label={t("passwordLabel")}>
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </FormField>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-ink/70 hover:text-ink"
          >
            {t("forgotLink")}
          </Link>
        </div>

        {error ? (
          <p className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" fullWidth loading={pending} size="lg">
          {t("loginCta")}
        </Button>
      </form>
    </AuthLayout>
  );
}
